"use client";
import React from "react";
import Image from "next/image";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import styles from "./aboutHeroSection.module.css";

export type AboutHeroSectionProps = {
  profileImageUrl?: string;
  title?: string;
  descriptionPart1?: string;
  descriptionPart2?: string;
};

const defaultTitle = "About Andrew Leger";
const defaultDescriptionPart1 =
  "I work in software engineering, product, and delivery. My experience spans application development, Agile leadership, and team management. I focus on simple systems that raise output and reduce friction across teams.";
const defaultDescriptionPart2 =
  "Software Engineering Leader · Agile Coach · Product Strategist";

const AboutHeroSection: React.FC<AboutHeroSectionProps> = ({
  profileImageUrl,
  title = defaultTitle,
  descriptionPart1 = defaultDescriptionPart1,
  descriptionPart2 = defaultDescriptionPart2,
}) => {
  const [sectionRef, isVisible] = useScrollAnimation();
  const [hasAnimated, setHasAnimated] = React.useState(false);

  React.useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isVisible, hasAnimated]);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`flex w-full items-start justify-center gap-8 md:gap-12 lg:gap-16 px-4 sm:px-6 lg:px-8 py-12 lg:py-24 relative ${hasAnimated ? styles.sectionAnimation : ""}`}
      aria-label="About hero section"
    >
      <div className="flex flex-col md:flex-row items-center md:items-start justify-center gap-8 md:gap-16 max-w-7xl w-full">
        {/* Profile Image */}
        {profileImageUrl && (
          <div className={`relative flex-shrink-0 ${hasAnimated ? styles.imageAnimation : ""}`}>
            <Image
              src={profileImageUrl}
              alt="Andrew Leger"
              width={210}
              height={210}
              className="relative w-[180px] h-[180px] sm:w-[210px] sm:h-[210px] aspect-square object-cover rounded-full"
              priority
            />
          </div>
        )}

        {/* Text Content */}
        <div className="flex flex-col max-w-2xl w-full items-center md:items-start gap-6 relative">
          <div className="flex flex-col items-start gap-4 relative self-stretch w-full">
            <h1 className={`relative self-stretch font-assistant font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-[64px] tracking-[-0.02em] md:tracking-[-1.28px] leading-tight md:leading-[76px] ${styles.aboutHeroTitleGradient} ${hasAnimated ? styles.titleAnimation : ""}`}>
              {title}
            </h1>
          </div>

          <p className={`relative self-stretch font-inter text-lg leading-7 ${hasAnimated ? styles.descriptionAnimation : ""}`}>
            <span className="font-semibold text-white">
              {descriptionPart1}
            </span>
          </p>
          <p className={`relative self-stretch font-inter text-lg leading-7 ${hasAnimated ? styles.descriptionAnimation : ""}`} style={hasAnimated ? { animationDelay: "0.4s" } : undefined}>
            <span className="font-normal text-slate-300">
              {descriptionPart2}
            </span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default AboutHeroSection;

